import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  containerTypeCopy,
  filmProcessDefaultsByProductType,
  filmProcessDefaultsByUseCase,
  guidanceSteps
} from '../config/guidanceQuestions';
import { getIncompatibleSealingMachineIdsForContainers } from '../utils/sealingCompatibility';
import {
  GUIDANCE_CONTAINER_TYPES,
  matchContainersForGuidance
} from '../utils/containerUtils';

const QUESTION_STEP_IDS = guidanceSteps.map((step) => step.id);
export const GUIDANCE_WIZARD_STEPS = [...QUESTION_STEP_IDS, 'film', 'summary'];

const INITIAL_ANSWERS = {
  productType: '',
  portionSize: '',
  compartments: '',
  useCase: ''
};

const arrayShallowEqual = (a, b) => {
  if (a.length !== b.length) return false;
  return a.every((item, index) => item === b[index]);
};

const deriveProcessDefaults = ({ productType, useCase }) => {
  const defaults = new Set();
  const useCaseDefaults = filmProcessDefaultsByUseCase[useCase] || [];
  useCaseDefaults.forEach((item) => defaults.add(item));
  const productDefaults = filmProcessDefaultsByProductType[productType] || [];
  productDefaults.forEach((item) => defaults.add(item));
  return Array.from(defaults);
};

export const useGuidanceState = ({
  normalizedContainers,
  deliContainers,
  filterFilms,
  filmProducts
}) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState(INITIAL_ANSWERS);
  const [filmProcesses, setFilmProcesses] = useState([]);
  const [filmSubstrate, setFilmSubstrate] = useState('');
  const [hasAdjustedProcesses, setHasAdjustedProcesses] = useState(false);
  const [summaryVersion, setSummaryVersion] = useState(0);

  useEffect(() => {
    if (hasAdjustedProcesses) return;
    const defaults = deriveProcessDefaults(answers);
    if (!defaults.length) {
      setFilmProcesses((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    setFilmProcesses((prev) => (arrayShallowEqual(prev, defaults) ? prev : defaults));
  }, [answers, hasAdjustedProcesses]);

  const containerMatches = useMemo(
    () =>
      matchContainersForGuidance({
        normalizedContainers,
        deliContainers,
        answers
      }),
    [answers, normalizedContainers, deliContainers]
  );

  const containerType = containerMatches.containerType;

  const recommendedMealContainers = useMemo(() => {
    if (!containerMatches?.groups || containerType !== GUIDANCE_CONTAINER_TYPES.MEAL) {
      return [];
    }
    return containerMatches.groups.flatMap((group) => group.items || []);
  }, [containerMatches, containerType]);

  const incompatibleSealingMachineIds = useMemo(() => {
    return getIncompatibleSealingMachineIdsForContainers(recommendedMealContainers);
  }, [recommendedMealContainers]);

  const recommendedFilms = useMemo(() => {
    if (!filmProcesses.length) return [];
    return filterFilms(filmProducts, filmProcesses, filmSubstrate || "I don't know");
  }, [filmProcesses, filmSubstrate, filterFilms, filmProducts]);

  const handleSelectAnswer = useCallback((key, value) => {
    setAnswers((prev) => {
      if (prev[key] === value) return prev;
      return {
        ...prev,
        [key]: value
      };
    });
  }, []);

  const currentStepId = GUIDANCE_WIZARD_STEPS[stepIndex];
  const currentQuestion = guidanceSteps.find((step) => step.id === currentStepId) || null;

  const goToStep = useCallback((stepId) => {
    const targetIndex = GUIDANCE_WIZARD_STEPS.indexOf(stepId);
    if (targetIndex === -1) return;
    setStepIndex(targetIndex);
  }, []);

  const goForward = useCallback(() => {
    setStepIndex((prev) => Math.min(prev + 1, GUIDANCE_WIZARD_STEPS.length - 1));
  }, []);

  const goBack = useCallback(() => {
    setStepIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const resetWizard = useCallback(() => {
    setAnswers(INITIAL_ANSWERS);
    setFilmProcesses([]);
    setFilmSubstrate('');
    setStepIndex(0);
    setHasAdjustedProcesses(false);
    setSummaryVersion(0);
  }, []);

  const toggleFilmProcess = useCallback((process) => {
    setHasAdjustedProcesses(true);
    setFilmProcesses((prev) => {
      if (prev.includes(process)) {
        return prev.filter((item) => item !== process);
      }
      return [...prev, process];
    });
  }, []);

  const isQuestionStepComplete = (stepId) => {
    if (stepId === 'film') {
      return filmProcesses.length > 0;
    }
    if (stepId === 'summary') {
      return true;
    }
    return Boolean(answers[stepId]);
  };

  const nextButtonEnabled = isQuestionStepComplete(currentStepId);

  const containerTypeMeta = containerTypeCopy[containerType] || containerTypeCopy.meal;

  const shouldHighlightDeliFilm = containerType === GUIDANCE_CONTAINER_TYPES.DELI;
  const recommendedContainerHeadline = containerTypeMeta.label;

  const summaryReady = QUESTION_STEP_IDS.every((id) => Boolean(answers[id])) && filmProcesses.length > 0;

  const containerRecommendationReason = useMemo(() => {
    const reasons = [];
    if (answers.productType) {
      const readable = guidanceSteps[0].options.find((opt) => opt.value === answers.productType);
      if (readable) reasons.push(`Product type: ${readable.label}`);
    }
    if (answers.portionSize) {
      const option = guidanceSteps[1].options.find((opt) => opt.value === answers.portionSize);
      if (option) reasons.push(`Portion size: ${option.label}`);
    }
    if (answers.compartments && answers.compartments !== 'notSure') {
      const option = guidanceSteps[2].options.find((opt) => opt.value === answers.compartments);
      if (option) reasons.push(`Compartments: ${option.label}`);
    }
    if (answers.useCase && answers.useCase !== 'notSure') {
      const option = guidanceSteps[3].options.find((opt) => opt.value === answers.useCase);
      if (option) reasons.push(`Use case: ${option.label}`);
    }
    return reasons;
  }, [answers]);

  useEffect(() => {
    if (GUIDANCE_WIZARD_STEPS[stepIndex] === 'summary') {
      setSummaryVersion((prev) => prev + 1);
    }
  }, [
    stepIndex,
    containerMatches,
    recommendedFilms,
    containerRecommendationReason,
    shouldHighlightDeliFilm,
    containerType
  ]);

  const filmProcessSummary = useMemo(
    () =>
      filmProcesses.map((process) => ({
        id: process,
        label: process
      })),
    [filmProcesses]
  );

  return {
    answers,
    containerMatches,
    containerRecommendationReason,
    containerType,
    containerTypeMeta,
    currentQuestion,
    currentStepId,
    filmProcessSummary,
    filmProcesses,
    filmSubstrate,
    goBack,
    goForward,
    goToStep,
    guidanceSteps,
    handleSelectAnswer,
    nextButtonEnabled,
    recommendedContainerHeadline,
    recommendedFilms,
    recommendedSummaryReady: summaryReady,
    resetWizard,
    setFilmSubstrate,
    toggleFilmProcess,
    shouldHighlightDeliFilm,
    incompatibleSealingMachineIds,
    summaryVersion
  };
};
